import React, { PropsWithChildren, useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { Employee } from "@/types";
import { Camera, Mail, MapPinned, Phone } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatAvatarName } from "@/utils";
import { FormCreateCollaborator } from "./form-create-collaborator";
import { FormUpdateCollaborator } from "./form-update-collaborator";
import { FormWorkTimesCollaborator } from "./form-worktimes-collaborator";
import { FormContractCollaborator } from "./form-contract-collaborator";
import { EmployeeUnavailabilityManager } from "./employee-unavailability-manager";

type FormCollaboratorProps = PropsWithChildren & {
  collaborator?: Employee;
  refetch?: () => void;
  disable?: boolean
};

const tabs = [
  {
    label: "Cadastro",
    value: "register",
  },
  {
    label: "Horário de trabalho",
    value: "worktimes",
  },
  {
    label: "Bloqueios de agenda",
    value: "unavailabilities",
  },
  {
    label: "Contratos",
    value: "contracts",
  },
];
export function FormCollaborator({
  collaborator,
  children,
  refetch,
  disable,
}: FormCollaboratorProps) {
  const [open, setOpen] = useState(false);

  return (
    <Drawer open={disable ? false : open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="h-[94dvh] overflow-hidden">
        <Tabs defaultValue="register" className="flex min-h-0 flex-1 flex-col pt-4">
          <DrawerHeader className="flex shrink-0 flex-col items-stretch gap-3 border-b text-left lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 space-y-1 pr-6">
              <DrawerTitle className="text-teal-700 font-bold">
                {collaborator ? "Editar" : "Novo"} colaborador
              </DrawerTitle>
              <DrawerDescription>
                Preencha os campos para {collaborator ? "editar" : "criar"} um
                colaborador.
              </DrawerDescription>
            </div>
            <div className="min-w-0 overflow-x-auto lg:mr-6">
              <TabsList className="w-max justify-start border-b">
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    disabled={tab.value !== "register" && !collaborator}
                    className="shrink-0 data-[state=active]:bg-teal-700 data-[state=active]:text-white"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          </DrawerHeader>
          <TabsContent value="register" className="mt-0 min-h-0 flex-1 overflow-y-auto">
            {collaborator ? (
              <FormUpdateCollaborator
                collaborator={collaborator}
                handleClose={() => setOpen(false)}
              />
            ) : (
              <FormCreateCollaborator handleClose={() => setOpen(false)} />
            )}
          </TabsContent>
          <TabsContent value="worktimes" className="min-h-0 flex-1 overflow-y-auto">
            {collaborator && (
              <FormWorkTimesCollaborator
                collaborator={collaborator}
                handleClose={() => setOpen(false)}
                refetch={refetch}
              />
            )}
          </TabsContent>
          <TabsContent value="unavailabilities" className="min-h-0 flex-1 overflow-y-auto">
            {collaborator && (
              <EmployeeUnavailabilityManager
                employeeId={collaborator.id}
                employeeName={collaborator.person.name}
              />
            )}
          </TabsContent>
          <TabsContent value="contracts" className="min-h-0 flex-1 overflow-y-auto">
            {collaborator && (
              <FormContractCollaborator collaborator={collaborator} />
            )}
          </TabsContent>
        </Tabs>
      </DrawerContent>
    </Drawer>
  );
}

type LayoutFormCollaboratorProps = PropsWithChildren & {
  callBack?: () => void;
  handleImageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  formik?: any;
  previewUrl?: string | null;
  collaboratorId?: string;
  headerButton?: React.ReactNode;
};
export function LayoutFormCollaborator({
  children,
  handleImageChange,
  formik,
  previewUrl,
  headerButton,
}: LayoutFormCollaboratorProps) {
  return (
    <section className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 flex-col gap-3 border-b p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-4 md:gap-7">
          <div className="relative shrink-0">
            <Avatar className="relative size-16 rounded-xl md:size-28">
              <AvatarImage src={previewUrl || ""} />
              <AvatarFallback>
                {formatAvatarName(formik?.values.name || "Avatar")}
              </AvatarFallback>
            </Avatar>
            <label
              htmlFor="avatar"
              className="cursor-pointer absolute right-0 -mr-2"
            >
              <Camera
                size={32}
                className="absolute bg-teal-700 p-1 rounded-[6px] text-white bottom-0 right-0"
              />
            </label>
            <input
              hidden
              id="avatar"
              type="file"
              accept=".png, .jpg"
              {...formik?.getFieldProps("avatar")}
              onChange={handleImageChange}
            />
          </div>
          <div className="min-w-0 space-y-2 break-words">
            <h3 className="text-base text-zinc-700 font-semibold">
              {formik?.values.name || "Nome do colaborador"}
            </h3>
            <div className="text-sm text-zinc-600 space-y-1">
              <p className="flex items-start gap-2">
                <Phone size={14} className="mt-1 shrink-0" />
                <span className="min-w-0 break-words">{formik?.values.phone || "Telefone do colaborador não informado"}</span>
              </p>
              <p className="flex items-start gap-2">
                <Mail size={14} className="mt-1 shrink-0" />
                <span className="min-w-0 break-words">{formik?.values.email || "E-mail não informado"}</span>
              </p>
              <p className="flex items-start gap-2">
                <MapPinned size={14} className="mt-1 shrink-0" />
                <span className="min-w-0 break-words">{formik?.values.address || "Endereço do profissional não informado"}</span>
              </p>
            </div>
          </div>
        </div>
        {headerButton && <div className="shrink-0 self-start">{headerButton}</div>}
      </header>
      {children}
    </section>
  );
}
